import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function useNavigationGuard(isDirty, onConfirm) {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPathRef = useRef(location.pathname);
    const isPopStateRef = useRef(false);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            isPopStateRef.current = true;
        };
        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    // Handle route changes (React Router)
    useEffect(() => {
        if (location.pathname !== currentPathRef.current) {
            if (isDirty) {
                const confirmLeave = onConfirm?.() ?? window.confirm("You have unsaved changes. Leave?");
                if (!confirmLeave) {
                    if (isPopStateRef.current) {
                        window.history.pushState(null, "", currentPathRef.current);
                    } else {
                        navigate(currentPathRef.current, { replace: true });
                    }
                    isPopStateRef.current = false;
                    return;
                }
            }

            // Accept route change: update last path
            currentPathRef.current = location.pathname;
            isPopStateRef.current = false;
        }
    }, [location.pathname, isDirty, onConfirm, navigate]);

    // Handle refresh / close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = ""; 
                return "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isDirty]);
}
